import { ApolloQueryResult } from '@apollo/client';
import { pipe } from 'fp-ts/function';
import * as Opt from 'fp-ts/Option';
import { QueryObserverResult } from 'react-query';
import { AnyEventObject, assign, createMachine } from 'xstate';

import { CloneCampaignResponse } from 'modules/ai/hooks/campaigns/types';
import { useUpdateTemplateMedium } from 'modules/ai/nba/hooks/useUpdateTemplateMedium';
import {
  CommonTemplateLocaleData,
  NBAContextState,
  NBAServiceListResponse,
} from 'modules/ai/nba/types';
import { executeLocaleRemoval } from 'modules/ai/nba/utils/removeTemplate';
import { Requested } from 'modules/ai/types';
import { rejectGraphqlError } from 'modules/ai/utils';
import { QueryCampaignByIdResponse } from 'modules/campaigns/editor/hooks';
import { UpdateCampaignPayload } from 'modules/campaigns/lib/hooks/useGenerateSaveCampaignPayload';
import { UpdateTemplatePayload } from 'modules/campaigns/lib/hooks/useGenerateSaveTemplatePayload';
import { GraphQLCampaign } from 'modules/campaigns/lib/types';
import { Locale } from 'modules/domains/template';
import { List } from 'modules/listIndex/types';
import {
  FetchTemplateSingleContentByIdQueryRequest,
  UseTemplateEditSingleContentById,
} from 'modules/templateEditor/hooks/useTemplateSingleContentEditById';
import { UpdateDefaultLocaleRequest } from 'modules/templateEditor/hooks/useUpdateDefaultLocale';
import { ApiResponse } from 'services/api';

export const CloneAndUpdateStates = {
  inactive: 'inactive',
  createList: 'createList',
  updateNBAService: 'updateNBAService',
  cloneCampaign: 'cloneCampaign',
  queryData: 'queryData',
  queryDataWithLocaleEnhancement: 'queryDataWithLocaleEnhancement',
  updateData: 'updateData',
  ready: 'ready',
  error: 'error',
  removeTemplateLocalesWithoutEnhancement: 'removeTemplateLocalesWithoutEnhancement',
  removeTemplateLocalesWithEnhancement: 'removeTemplateLocalesWithEnhancement',
  updateDefaultTemplateLocale: 'updateDefaultTemplateLocale',
};

export interface CloneAndUpdateMachineContext {
  clonedData: Record<string, number>;
  hasRemovedLocaleErrors: boolean;
  listId: number;
}
export const defaultContext = {
  clonedData: {},
  // NOTE: Update CloneAndUpdateMachinePlan to use parallel states to handle locale work.
  hasRemovedLocaleErrors: false,
  listId: 0,
};
export const CloneAndUpdateMachinePlan = {
  predictableActionArguments: true,
  id: 'cloneAndUpdateStates',
  initial: CloneAndUpdateStates.inactive,
  context: {
    ...defaultContext,
  },
  states: {
    [CloneAndUpdateStates.inactive]: {
      on: {
        [CloneAndUpdateStates.createList]: {
          target: CloneAndUpdateStates.createList,
        },
        [CloneAndUpdateStates.error]: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.ready]: {
      on: {
        [CloneAndUpdateStates.createList]: {
          target: CloneAndUpdateStates.createList,
        },
      },
    },
    [CloneAndUpdateStates.error]: {
      entry: ['handleDeleteList'],
      on: {
        [CloneAndUpdateStates.createList]: {
          target: CloneAndUpdateStates.createList,
        },
        [CloneAndUpdateStates.inactive]: {
          target: CloneAndUpdateStates.inactive,
        },
      },
    },
    [CloneAndUpdateStates.createList]: {
      invoke: {
        src: 'createList',
        onDone: {
          target: CloneAndUpdateStates.updateNBAService,
          actions: 'handleListCreation',
        },
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.updateNBAService]: {
      invoke: {
        src: 'updateNBAService',
        onDone: {
          target: CloneAndUpdateStates.cloneCampaign,
        },
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.cloneCampaign]: {
      invoke: {
        src: 'cloneCampaign',
        onDone: [
          {
            target: CloneAndUpdateStates.updateDefaultTemplateLocale,
            actions: 'handleCloneData',
            cond: 'localesFeatureEnabled',
          },
          {
            target: CloneAndUpdateStates.removeTemplateLocalesWithoutEnhancement,
            actions: 'handleCloneData',
          },
        ],
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.queryData]: {
      invoke: {
        src: 'queryCampaignAndContentData',
        onDone: {
          target: CloneAndUpdateStates.updateData,
        },
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.queryDataWithLocaleEnhancement]: {
      invoke: {
        src: 'queryCampaignAndContentDataWithLocaleEnhancement',
        onDone: {
          target: CloneAndUpdateStates.updateData,
        },
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.updateData]: {
      invoke: {
        src: 'mutateCampaignAndContentData',
        onDone: {
          target: CloneAndUpdateStates.ready,
          actions: 'redirectToCampaign',
        },
        onError: {
          target: CloneAndUpdateStates.error,
        },
      },
    },
    [CloneAndUpdateStates.removeTemplateLocalesWithoutEnhancement]: {
      invoke: {
        src: 'removeTemplateLocalesWithoutEnhancement',
        onDone: {
          target: CloneAndUpdateStates.queryData,
        },
        onError: {
          target: CloneAndUpdateStates.queryData,
        },
      },
    },
    [CloneAndUpdateStates.updateDefaultTemplateLocale]: {
      invoke: {
        src: 'updateDefaultTemplateLocale',
        onDone: {
          target: CloneAndUpdateStates.removeTemplateLocalesWithEnhancement,
        },
        onError: {
          target: CloneAndUpdateStates.removeTemplateLocalesWithEnhancement,
        },
      },
    },
    [CloneAndUpdateStates.removeTemplateLocalesWithEnhancement]: {
      invoke: {
        src: 'removeTemplateLocalesWithEnhancement',
        onDone: {
          target: CloneAndUpdateStates.queryDataWithLocaleEnhancement,
        },
        onError: {
          target: CloneAndUpdateStates.queryDataWithLocaleEnhancement,
        },
      },
    },
  },
};

export const CloneAndUpdateMachine =
  createMachine<CloneAndUpdateMachineContext>(CloneAndUpdateMachinePlan);

export type CloneAndUpdateMachineEffects = {
  /**
  @description Campaign data transformations applied *before* sending to the useGenerateSaveCampaignPayload payload creator
  */
  applyCampaignDataTransformer: (
    campaign: GraphQLCampaign,
    listId: number,
  ) => UpdateCampaignPayload;
  /**
  @description Template data transformations applied *after* sending to the useGenerateSaveTemplatePayload payload creator
  */
  applyTemplateDataTransformer: (
    campaignData: GraphQLCampaign,
    templateData: UseTemplateEditSingleContentById,
  ) => UpdateTemplatePayload;
  /**
  @description Effect to perform the clone campaign and template operation
  */
  cloneCampaignMutation: () => Promise<
    QueryObserverResult<Requested<CloneCampaignResponse>, unknown>
  >;
  /**
  @description Effect to create the nba audience list
  */
  createAudienceList: () => Promise<ApiResponse<Record<string, any>>>;
  /**
   @description Update NBA service with created list
   */
  updateNBAService: (listId: number) => Promise<ApiResponse<NBAServiceListResponse>>;
  /**
  @description Fallback effect to delete the nba audience list created in this hook upon error
  */
  deleteAudienceList: ({ list }: { list: Pick<List, 'id'> }) => Promise<ApiResponse>;
  /**
   @description Function to retrieve message medium appropriate template mutation
   */
  getTemplateUpdater: ReturnType<typeof useUpdateTemplateMedium>;
  /**
  @description Effect to fetch the cloned campaign data
  */
  fetchCampaign: ({ id }: { id: number }) => Promise<ApolloQueryResult<QueryCampaignByIdResponse>>;
  /**
  @description Effect to fetch the cloned template data
  */
  fetchTemplate: ({
    id,
  }: Partial<FetchTemplateSingleContentByIdQueryRequest>) => Promise<UseTemplateEditSingleContentById>;
  /**
  @description Effect redirect the user to the campaign editor upon success
  */
  onRedirect: (redirectUrl: string) => void;
  /**
  @description Effect to persist original campaign and template data to the cloned campaign and template alongside AI data changes
  */
  saveCampaignData: (campaignUpdate: { data: UpdateCampaignPayload }) => Promise<unknown>;
  templateLocaleData: NBAContextState['templateLocaleData'];
  removeLocale: (templateId: number, locale: string) => Promise<unknown>;
  updateDefaultLocale: (payload: { variables: UpdateDefaultLocaleRequest }) => Promise<unknown>;
};
export const createCloneAndUpdateMachineEffects = ({
  applyCampaignDataTransformer,
  applyTemplateDataTransformer,
  cloneCampaignMutation,
  createAudienceList,
  deleteAudienceList,
  fetchCampaign,
  fetchTemplate,
  getTemplateUpdater,
  templateLocaleData,
  onRedirect,
  removeLocale,
  saveCampaignData,
  updateNBAService,
  updateDefaultLocale,
}: CloneAndUpdateMachineEffects) => ({
  actions: {
    handleListCreation: assign<CloneAndUpdateMachineContext>(
      (_: CloneAndUpdateMachineContext, event: AnyEventObject) => {
        return {
          listId: event.data.listId,
        };
      },
    ),
    handleCloneData: assign<CloneAndUpdateMachineContext>((_, event: AnyEventObject) => {
      return {
        clonedData: event.data.data,
      };
    }),
    redirectToCampaign: (context: CloneAndUpdateMachineContext) => {
      onRedirect(`/campaigns/${context.clonedData.clonedCampaignId}`);
    },
    handleDeleteList: assign<CloneAndUpdateMachineContext>(
      (context: CloneAndUpdateMachineContext) => {
        if (context.listId === defaultContext.listId) {
          return context;
        }
        deleteAudienceList({ list: { id: context.listId } });
        return {
          ...context,
          listId: defaultContext.listId,
        };
      },
    ),
  },
  guards: {
    localesFeatureEnabled: () =>
      pipe(
        templateLocaleData,
        Opt.map<CommonTemplateLocaleData, CommonTemplateLocaleData['isLocalesFeatureEnabled']>(
          (localeData) => localeData.isLocalesFeatureEnabled,
        ),
        Opt.getOrElse(() => false),
      ),
  },
  services: {
    createList: () => createAudienceList(),
    cloneCampaign: () => cloneCampaignMutation(),
    updateNBAService: (context: CloneAndUpdateMachineContext) =>
      new Promise((resolve) => setTimeout(resolve, 3000)).then(() =>
        updateNBAService(context.listId),
      ),
    queryCampaignAndContentData: (context: CloneAndUpdateMachineContext) => {
      return Promise.all([
        pipe({ id: context.clonedData.clonedCampaignId }, fetchCampaign, rejectGraphqlError),
        pipe({ id: context.clonedData.clonedTemplateId }, fetchTemplate, rejectGraphqlError),
      ]);
    },
    queryCampaignAndContentDataWithLocaleEnhancement: (context: CloneAndUpdateMachineContext) => {
      // need to set id and locale for fetch call to grab appropriate locale payload for update step
      const fetchTemplateVariablesWithLocale = pipe(
        templateLocaleData,
        Opt.map<CommonTemplateLocaleData, { id: number; locale?: { name: string } }>(
          (maybeLocaleData) => {
            // we need to ensure that if locale is none we don't pass in the locale key, that will cause CorruptedTemplateData error
            return pipe(
              maybeLocaleData.templateDefaultLocaleToSet,
              Opt.map((maybeTemplateDefaultLocaleTest) => {
                return {
                  id: context.clonedData.clonedTemplateId,
                  locale: {
                    name: maybeTemplateDefaultLocaleTest,
                  },
                };
              }),
              Opt.getOrElse(() => ({ id: context.clonedData.clonedTemplateId })),
            );
          },
        ),
        Opt.getOrElse(() => ({ id: context.clonedData.clonedTemplateId })),
      );

      return Promise.all([
        pipe({ id: context.clonedData.clonedCampaignId }, fetchCampaign, rejectGraphqlError),
        pipe(fetchTemplateVariablesWithLocale, fetchTemplate, rejectGraphqlError),
      ]);
    },
    mutateCampaignAndContentData: (
      context: CloneAndUpdateMachineContext,
      event: AnyEventObject,
    ) => {
      const [campaignResult, templateResult] = event.data;
      const campaignData = campaignResult.data.campaign;
      const templateData = templateResult.data;
      const updateTemplate = getTemplateUpdater(campaignData.messageMedium);

      const templatePayload = applyTemplateDataTransformer(campaignData, templateData);
      return new Promise((resolve) => setTimeout(resolve, 3000)).then(() =>
        Promise.all([
          saveCampaignData({
            data: applyCampaignDataTransformer(campaignData, context.listId),
          }),
          updateTemplate({
            variables: templatePayload as any,
          }),
        ]),
      );
    },
    removeTemplateLocalesWithoutEnhancement: (context: CloneAndUpdateMachineContext) => {
      return executeLocaleRemoval(
        removeLocale,
        context.clonedData.clonedTemplateId,
        pipe(
          templateLocaleData,
          Opt.map<CommonTemplateLocaleData, CommonTemplateLocaleData['templates']>(
            (localeData) => localeData.templates,
          ),
        ),
      );
    },
    updateDefaultTemplateLocale: async (context: CloneAndUpdateMachineContext) => {
      return pipe(
        templateLocaleData,
        Opt.chain<CommonTemplateLocaleData, string>(
          (localeData) => localeData.templateDefaultLocaleToSet,
        ),
        Opt.map<string, Promise<unknown>>((maybeLocaleToSet) => {
          return updateDefaultLocale({
            variables: {
              templateId: context.clonedData.clonedTemplateId,
              locale: { name: maybeLocaleToSet },
            },
          });
        }),
        Opt.getOrElse(() => Promise.resolve(<unknown>{})),
      );
    },
    removeTemplateLocalesWithEnhancement: (context: CloneAndUpdateMachineContext) => {
      const localesToRemove = pipe(
        templateLocaleData,
        Opt.chain<CommonTemplateLocaleData, Partial<Locale>[]>(
          (localeData) => localeData?.templatesLocalesToRemove,
        ),
      );

      return executeLocaleRemoval(
        removeLocale,
        context.clonedData.clonedTemplateId,
        localesToRemove,
      );
    },
  },
});
